import { useTheme } from '@emotion/react';
import React__default, { forwardRef, useCallback } from 'react';
import { useId } from 'react-id-generator';
import ReactSelect, { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import CreatableSelect from 'react-select/creatable';
import { Label } from '../Label/index.js';
import { Message } from '../Message/index.js';
import { selectStyles, SelectWrapper, OptionLabel, OptionIconWrapper, OptionTextWrapper, SubLabel, ClearIndicator, IndicatorIcon, MultiValueRemove } from './styles.js';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import { cssShorthandPropsFilter } from '../../primitives/Spacer/index.js';

function formatOptionLabel({
  label,
  icon,
  subLabel,
  'aria-label': ariaLabel,
  selectedLabel
}, {
  context
}) {
  return jsxs(OptionLabel, {
    "aria-label": ariaLabel || typeof label === 'string' && label,
    children: [icon && jsx(OptionIconWrapper, {
      children: typeof icon === 'string' ? jsx("img", {
        src: icon,
        alt: ""
      }) : icon
    }), jsxs(OptionTextWrapper, {
      justifyContent: "center",
      flexDirection: "column",
      children: [selectedLabel && context === 'value' ? selectedLabel : label, Boolean(subLabel) && jsx(SubLabel, {
        className: "form-sublabel",
        mt: 0.5,
        children: subLabel
      })]
    })]
  });
}
const CustomClearIndicator = props => jsx(components.ClearIndicator, {
  ...props,
  children: jsx(ClearIndicator, {
    size: props.selectProps.size
  })
});
const CustomDropdownIndicator = props => props.selectProps.hideDropdownIndicator ? null : jsx(components.DropdownIndicator, {
  ...props,
  children: jsx(IndicatorIcon, {
    size: props.selectProps.size,
    disabled: props.selectProps.disabled
  })
});
const CustomMultiValueRemove = props => jsx(components.MultiValueRemove, {
  ...props,
  children: jsx(MultiValueRemove, {})
});
function isMultiValue(arg) {
  return Array.isArray(arg);
}
function isGroupedOption(arg) {
  return 'options' in arg;
}
const Select = forwardRef(function SingleSelect({
  onChange,
  value,
  options,
  ...props
}, ref) {
  const handleChange = newValue => {
    if (isMultiValue(newValue)) {
      throw new Error('onChange value returned an array. If you want to use multi-select, please use the <MultiSelect/> component');
    } else {
      onChange(newValue === null || newValue === void 0 ? void 0 : newValue.value);
    }
  };
  const valueProp = React__default.useMemo(() => {
    if (options) {
      for (let i = 0; i < options.length; i++) {
        const option = options[i];
        if (isGroupedOption(option)) {
          const selectedOption = option.options.find(opt => opt.value === value);
          if (selectedOption) return selectedOption;
        } else if (option.value === value) {
          return option;
        }
      }
    }
    return null;
  }, [options, value]);
  return jsx(BaseSelect, {
    isMulti: false,
    value: valueProp,
    options: options,
    onChange: handleChange,
    ...props,
    ref: ref
  });
});
const MultiSelect = forwardRef(function MultiSelect({
  onChange,
  value,
  options,
  ...props
}, ref) {
  const handleChange = newValue => {
    if (Array.isArray(newValue)) {
      const newValues = newValue.map(({
        value
      }) => value);
      onChange(newValues);
    }
  };
  const instanceOfSelectOptionProps = useCallback(object => 'value' in object, []);
  const valueProp = React__default.useMemo(() => {
    if (options !== null && options !== void 0 && options.length && instanceOfSelectOptionProps(options[0]) && value !== null && value !== void 0 && value.length) {
      return options.filter(option => value.includes(option.value));
    }
    return null;
  }, [options, value, instanceOfSelectOptionProps]);
  return jsx(BaseSelect, {
    isMulti: true,
    value: valueProp,
    options: options,
    onChange: handleChange,
    ref: ref,
    ...props
  });
});
const BaseSelect = forwardRef(function BaseSelect({
  'data-testid': dataTestId,
  asyncLoadOptions,
  autoComplete = 'nope',
  className,
  defaultMenuIsOpen,
  disabled,
  error,
  filterOption,
  id,
  isClearable,
  isCreatable,
  isMulti,
  isSearchable,
  options = [],
  label,
  menuIsOpen,
  menuPortalTarget,
  message,
  onBlur,
  onChange,
  onMenuClose,
  onMenuOpen,
  formatOptionLabel: customFormatOptionLabel,
  formatGroupLabel,
  components: customSelectComponents = {},
  customSelectStyles,
  placeholder,
  size = 'md',
  hideDropdownIndicator,
  value,
  name,
  creatableProps = {},
  onInputChange,
  ...props
}, ref) {
  const [uniqueId] = useId(1, 'select-');
  const AUTO = 'auto';
  const inputId = id || uniqueId;
  const theme = useTheme();
  const NO_RESULTS_FOUND = theme.translate('Select.noResults');
  const SELECT_OPTION = theme.translate('Select.selectOption');
  const reactBaseSelectProps = {
    autoComplete,
    closeMenuOnSelect: !isMulti,
    components: {
      ClearIndicator: CustomClearIndicator,
      DropdownIndicator: CustomDropdownIndicator,
      MultiValueRemove: CustomMultiValueRemove,
      ...customSelectComponents
    },
    'data-testid': `${dataTestId}-select`,
    defaultMenuIsOpen,
    formatGroupLabel,
    formatOptionLabel: customFormatOptionLabel || formatOptionLabel,
    inputId,
    isDisabled: disabled,
    isSearchable,
    maxMenuHeight: 284,
    menuIsOpen,
    menuPlacement: AUTO,
    menuPortalTarget,
    onBlur,
    onChange,
    onMenuClose,
    onMenuOpen,
    placeholder: placeholder || SELECT_OPTION,
    isMulti,
    styles: {
      ...selectStyles(theme),
      ...customSelectStyles
    },
    name,
    onInputChange
  };
  const selectProps = {
    error,
    message,
    size,
    value,
    hideDropdownIndicator: hideDropdownIndicator ? 'true' : ''
  };
  const hasMessage = Boolean(message) || Boolean(error);
  return jsxs(SelectWrapper, {
    "data-testid": dataTestId,
    className: className,
    size: size,
    ...cssShorthandPropsFilter(props),
    children: [jsx(Label, {
      disabled: disabled,
      htmlFor: inputId,
      mb: 1,
      children: label
    }), asyncLoadOptions && jsx(AsyncSelect, {
      defaultOptions: options,
      filterOption: filterOption,
      isClearable: isClearable,
      loadOptions: asyncLoadOptions,
      noOptionsMessage: () => NO_RESULTS_FOUND,
      ref: ref,
      ...reactBaseSelectProps,
      ...selectProps
    }), !asyncLoadOptions && isCreatable && jsx(CreatableSelect, {
      filterOption: filterOption,
      options: options,
      ref: ref,
      ...reactBaseSelectProps,
      ...creatableProps,
      ...selectProps
    }), !asyncLoadOptions && !isCreatable && jsx(ReactSelect, {
      filterOption: filterOption,
      noOptionsMessage: () => NO_RESULTS_FOUND,
      options: options,
      ref: ref,
      ...reactBaseSelectProps,
      ...selectProps
    }), hasMessage && jsx(Message, {
      disabled: disabled,
      error: error,
      message: message,
      mt: 1
    })]
  });
});

export { MultiSelect, Select };
